import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { SectionHeading } from './SectionHeading';

const FIELDS = [
  ['pharmacy_name', 'Pharmacy name', 'text', true, 160, 'organization'],
  ['contact_name', 'Your name', 'text', true, 120, 'name'],
  ['email', 'Email address', 'email', true, 160, 'email'],
  ['phone', 'Phone / WhatsApp', 'tel', false, 40, 'tel'],
  ['town', 'Town or city', 'text', false, 120, 'address-level2'],
  ['branches', 'Number of branches', 'number', false],
  ['current_software', 'What you use today', 'text', false, 160],
];

const leadErrorMessage = (detail) => {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const issue = detail.find((item) => typeof item?.msg === 'string');
    if (issue) {
      const key = Array.isArray(issue.loc) ? issue.loc[issue.loc.length - 1] : '';
      const label = FIELDS.find(([field]) => field === key)?.[1];
      return `${label ? `${label}: ` : ''}${issue.msg}`;
    }
  }
  return 'We couldn’t confirm your request. Please try again, or contact sales@dutilyne.com.';
};

export const DemoForm = () => {
  const [form, setForm] = useState({ pharmacy_name: '', contact_name: '', email: '', phone: '', town: '', branches: 1, current_software: '', interest: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const feedback = useRef(null);
  useEffect(() => { if (done || error) feedback.current?.focus(); }, [done, error]);
  const submit = async (event) => {
    event.preventDefault();
    if (inFlight.current) return;
    setError('');
    if (form.pharmacy_name.trim().length < 2 || form.contact_name.trim().length < 2) {
      setError('Please enter a pharmacy name and your name, each with at least two characters.');
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value]));
      const response = await api.post('/public/leads', { ...body, branches: Number(form.branches) || 1 });
      if (!response.data?.lead_id) throw new Error('Receipt was not confirmed');
      setDone(true);
      toast.success('Your demo request has been received.');
    } catch (err) {
      setError(leadErrorMessage(err?.response?.data?.detail));
    } finally { setBusy(false); inFlight.current = false; }
  };

  if (done) return <div className="wl-form-success" tabIndex={-1} ref={feedback} role="status" data-testid="demo-request-done"><CheckCircle2 size={38} /><h3>Your next chapter starts here.</h3><p>Thank you — your demo request has been received by DutiLyne. Your contact details and pharmacy information have been saved for follow-up.</p><p className="wl-small">No account has been created and nothing has been charged.</p></div>;
  return <form className="wl-form" onSubmit={submit} aria-busy={busy} data-testid="demo-request-form">
    {FIELDS.map(([key, label, type, required, maxLength, autoComplete]) => <div key={key} className={key === 'current_software' ? 'wl-span' : ''}><label htmlFor={`lead-${key}`}>{label}{required && <span aria-hidden="true"> *</span>}{!required && <span className="wl-optional">optional</span>}</label>
      <Input id={`lead-${key}`} className="wl-input" type={type} required={required} minLength={required && type === 'text' ? 2 : undefined} maxLength={maxLength} autoComplete={autoComplete} min={type === 'number' ? 1 : undefined} max={type === 'number' ? 500 : undefined} step={type === 'number' ? 1 : undefined} value={form[key]} disabled={busy} onChange={(event) => setForm({ ...form, [key]: event.target.value })} data-testid={`lead-${key}`} /></div>)}
    <div className="wl-span"><label htmlFor="lead-interest">What would you like to explore?<span className="wl-optional">optional</span></label><Textarea id="lead-interest" className="wl-input" rows={3} maxLength={1000} value={form.interest} disabled={busy} onChange={(event) => setForm({ ...form, interest: event.target.value })} data-testid="lead-interest" aria-describedby="lead-no-patient-data" /><p id="lead-no-patient-data" className="wl-small">Please don’t include patient details or medical records.</p></div>
    {error && <p role="alert" tabIndex={-1} ref={feedback} className="wl-error wl-span" data-testid="demo-request-error">{error}</p>}
    <div className="wl-span wl-form-submit"><Button className="wl-button wl-button-primary" type="submit" disabled={busy} data-testid="lead-submit">{busy ? <><Loader2 className="animate-spin" />Sending request…</> : <>Request a demo <ArrowRight /></>}</Button><p className="wl-small">No account created. No payment required.</p></div>
  </form>;
};

export const DemoRequest = () => (
  <section className="wl-section wl-demo-request" id="demo" aria-labelledby="request-heading">
    <div className="wl-wrap wl-request-grid"><div>
      <SectionHeading id="request-heading" eyebrow="LET’S BUILD A BETTER PHARMACY DAY" title={<>Your patients.<br />Your practice.<br /><span>More possibilities.</span></>}>Tell us a little about your pharmacy. Let’s explore how DutiLyne could fit the way your team works.</SectionHeading>
      <ul className="wl-request-promises"><li><Check size={17} /> A conversation about your workflows</li><li><Check size={17} /> A closer look at care and operations</li><li><Check size={17} /> Clear answers on offline capabilities</li></ul>
      <span className="wl-request-note">Community pharmacy. Connected care.</span>
    </div><DemoForm /></div>
  </section>
);